import React from 'react';
import {safelySetInnerHTML} from '@udemy-v2/utils';
import {CoursePrice} from '@udemy-v2/react-course-pricing';
import {SubscriptionPlanContext, ConsumerSubscriptionPlan} from '@udemy-v2/subscription-graphql';
import {
    REVIEW_MAX_RATING,
    REVIEW_MIN_RATING,
} from '@/features/course-landing-page/components/rating-display/constants';
import {getDifficultyLevel} from '@/app/lib/course/get-difficulty-level';
import styles from './course-seo.module.scss';

import {Course, CourseCategory} from '@udemy-v2/browse-course';

export function getDurationData(durationInSeconds: number | undefined) {
    if (!durationInSeconds) {
        return undefined;
    }

    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);

    let duration = 'PT';
    if (hours > 0) {
        duration += `${hours}H`;
    }
    if (minutes > 0) {
        duration += `${minutes}M`;
    }

    return duration === 'PT' ? undefined : duration;
}

export function getBreadcrumbs(course: Course) {
    const breadcrumbs = (course.categories ?? []).map(
        (category: CourseCategory, index: number) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: category.name,
            item: `https://www.udemy.com${category.url}`,
        }),
    );

    if (breadcrumbs.length === 0) {
        return null;
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs,
    };
}

export enum OfferCategory {
    PAID = 'Paid',
    SUBSCRIPTION = 'Subscription',
    FREE = 'Free',
}

interface Offer {
    '@type': 'Offer';
    category: OfferCategory;
    price?: number;
    priceCurrency?: string;
}

export interface GetCourseOffersParams {
    course: Course;
    coursePrice?: CoursePrice;
    subscriptionContext?: SubscriptionPlanContext<ConsumerSubscriptionPlan>;
}

/**
 * Generate course offers for SEO schema (server-side compatible)
 * Note: Subscription offers are now handled client-side in CourseSeoPricing
 */
export function getCourseOffers({
    course,
    coursePrice,
    subscriptionContext,
}: GetCourseOffersParams): Offer[] {
    const offers: Offer[] = [];

    if (course.isFree) {
        offers.push({
            '@type': 'Offer',
            category: OfferCategory.FREE,
        });
        return offers;
    }

    if (coursePrice) {
        offers.push({
            '@type': 'Offer',
            category: OfferCategory.PAID,
            price: coursePrice.price.amount,
            priceCurrency: coursePrice.price.currency,
        });
    }

    // Subscription offers are handled client-side via SubscriptionContext
    // Currently hardcoded to undefined - will be implemented later
    if (subscriptionContext) {
        offers.push({'@type': 'Offer', category: OfferCategory.SUBSCRIPTION});
    }

    return offers;
}

export function getSyllabusSections(course: Course) {
    return (
        course.curriculum?.sections.slice(0, 50).map((section) => ({
            '@type': 'Syllabus',
            name: section.title,
            timeRequired: getDurationData(section.contentLength),
        })) ?? []
    );
}

export function getCourseAboutInfo(course: Course) {
    const primaryCategory = course.categories?.[0]?.name;
    return primaryCategory ? [primaryCategory] : [];
}

//const daysAgo = (days: number) => Date.now() - days * 24 * 60 * 60 * 1000;

/**
 * Generates the base course schema without pricing data
 * This can be rendered server-side for ISR compatibility
 */
export function generateCourseSchema(course: Course, coursePrice?: CoursePrice) {
    const instructors =
        course.instructors
            .map((instructor) => ({
                '@type': 'Person',
                name: instructor.name || '',
            }))
            .filter((i) => i.name) ?? [];

    const firstInstructor = instructors[0];
    const provider = firstInstructor
        ? {
              provider: {
                  '@type': 'Organization',
                  name: firstInstructor.name,
                  sameAs: `https://www.udemy.com/user/${encodeURIComponent(firstInstructor.name)}/`,
              },
          }
        : {};

    const courseSchema = {
        '@context': 'https://schema.org',
        '@type': 'Course',
        '@id': course.urlCourseLanding,
        about: {
            '@type': 'Thing',
            name: getCourseAboutInfo(course),
        },
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: course.rating.average?.toFixed(1),
            ratingCount: course.rating.count,
            bestRating: REVIEW_MAX_RATING,
            worstRating: REVIEW_MIN_RATING,
            reviewCount: course.rating.count,
        },
        audience: {
            '@type': 'Audience',
            audienceType: course.targetAudience,
        },
        author: instructors,
        datePublished: course.updatedOn,
        description: course.headline || course.description,
        educationalCredentialAwarded: {
            '@type': 'EducationalOccupationalCredential',
            name: 'Udemy Certificate',
            credentialCategory: 'Certificate',
            url: 'https://support.udemy.com/hc/en-us/sections/360011037194-Certificates-of-Completion?type=student',
        },
        educationalLevel: getDifficultyLevel(course.level),
        hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'Online',
            courseWorkload: getDurationData(course.durationInSeconds),
            instructor: instructors,
        },
        image: course.images.px480x270,
        inLanguage: course.locale,
        isAccessibleForFree: course.isFree,
        name: course.title,
        offers: getCourseOffers({
            course,
            coursePrice,
            subscriptionContext: undefined, // Hardcoded to undefined for now
        }),
        ...provider,
        publisher: {
            '@type': 'Organization',
            name: 'Udemy',
            sameAs: 'https://en.wikipedia.org/wiki/Udemy',
            url: 'https://www.udemy.com',
        },
        syllabusSections: getSyllabusSections(course),
        availableLanguage: course.captions?.map(
            (caption) => `${caption.locale} ${caption.isAutoGenerated ? '[Auto]' : '[CC]'}`,
        ),
        teaches: course.learningOutcomes.slice(0, 8),
        url: course.urlCourseLanding,
    };

    const breadcrumbSchema = getBreadcrumbs(course);

    return {
        '@context': 'https://schema.org',
        '@graph': [courseSchema, breadcrumbSchema].filter(Boolean),
    };
}

export interface CourseSeoProps {
    course: Course;
}

/**
 * Server-side component that renders SEO schema without pricing data
 * This is ISR-compatible as it doesn't use any hooks
 */
export function CourseSeoServer({course}: CourseSeoProps) {
    const schema = generateCourseSchema(course);
    const schemaString = JSON.stringify(schema);

    const schemaMarkup = (
        <div id="schema_markup" data-purpose="schema_markup">
            <script
                type="application/ld+json"
                {...safelySetInnerHTML({
                    descriptionOfCaller: 'course-landing-page/seo-info',
                    html: schemaString,
                })}
            />
        </div>
    );

    const modifiedDateEnabled = false; //        course.updatedOn && new Date(course.updatedOn).valueOf() > daysAgo(60);
    const modifiedDate = !modifiedDateEnabled ? null : (
        <div className="seo-info" itemScope={true} itemType="http://schema.org/CreativeWork">
            <span className={styles.hidden} itemProp="dateModified">
                {course.updatedOn}
            </span>
        </div>
    );

    return (
        <>
            {modifiedDate}
            {schemaMarkup}
        </>
    );
}

/**
 * Note: CourseSeo wrapper component removed for ISR compatibility.
 * Components now rendered separately:
 * - CourseSeoServer: Rendered at page level for ISR compatibility
 * - CourseSeoPricing: Rendered in client-side provider tree for hook access
 */
